import { useUsersMap } from "@tellescope/tanstack-query";
import { DeletableBadge, cn } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIsUserOutOfOfficeNow } from "./useIsUserOutOfOfficeNow";
import type { MentionTarget } from "./useMentions";

export interface MentionsBadgeRowProps {
  pendingMentions: MentionTarget[];
  /** IDs already in the conversation (assignedTo / userIds). Used to mark chips as "[New]". */
  existingUserIds: string[];
  onRemove: (target: MentionTarget) => void;
}

const isNewToConversation = (target: MentionTarget, existingUserIds: string[]): boolean => {
  if (target.type === "user") return !existingUserIds.includes(target.user.id);
  // A group is "new" if any of its members aren't already in the conversation
  return target.userIds.some((id) => !existingUserIds.includes(id));
};

export const MentionsBadgeRow = ({ pendingMentions, existingUserIds, onRemove }: MentionsBadgeRowProps) => {
  const { t } = useTranslation("team-chat");
  const usersMap = useUsersMap();
  const isUserOutOfOffice = useIsUserOutOfOfficeNow();

  const mentionsWithOutOfOfficeStatus = useMemo(() => {
    const isTargetOutOfOffice = (target: MentionTarget): boolean => {
      if (target.type === "user") return isUserOutOfOffice(target.user);
      return target.userIds.some((id) => {
        const user = usersMap[id];
        return !!user && isUserOutOfOffice(user);
      });
    };
    return pendingMentions.map((target) => ({ target, isOutOfOffice: isTargetOutOfOffice(target) }));
  }, [pendingMentions, usersMap, isUserOutOfOffice]);

  if (pendingMentions.length === 0) return null;

  const hasNewMentions = pendingMentions.some((m) => isNewToConversation(m, existingUserIds));

  const outOfOfficeNames = mentionsWithOutOfOfficeStatus.reduce<string[]>((acc, { target, isOutOfOffice }) => {
    if (isOutOfOffice) acc.push(target.type === "user" ? formatName(target.user) : target.name);
    return acc;
  }, []);

  return (
    <div className="px-4 pt-2 pb-1 border-t border-t-border space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">{t("Mentions")}:</span>
        {mentionsWithOutOfOfficeStatus.map(({ target, isOutOfOffice }) => {
          const isNew = isNewToConversation(target, existingUserIds);
          const label = target.type === "user" ? formatName(target.user) : target.name;
          return (
            <DeletableBadge
              key={target.type === "user" ? target.user.id : `group:${target.name}`}
              variant={isNew ? "default" : "secondary"}
              onDelete={() => onRemove(target)}
              className={cn(isOutOfOffice && "border-amber-300 bg-amber-100 text-amber-800")}
            >
              {isNew && <span className="mr-1 opacity-70">{t("[New]")}</span>}
              {label}
              {isOutOfOffice && t(" (out of office)")}
            </DeletableBadge>
          );
        })}
      </div>
      {hasNewMentions && (
        <p className="text-xs text-muted-foreground">
          {t("New mentions will be added to the conversation and can read prior messages")}
        </p>
      )}
      {outOfOfficeNames.length > 0 && (
        <p className="text-xs text-amber-600">
          {outOfOfficeNames.length === 1
            ? t("{{name}} is currently out of office", { name: outOfOfficeNames[0] })
            : t("Currently out of office: {{names}}", { names: outOfOfficeNames.join(", ") })}
        </p>
      )}
    </div>
  );
};
